import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from '../../app/store';
import { get } from '../../services/BackendService';
import { RegistrationRedirectError } from '../../components/registration/registration.interface';
import { setRedirectError } from '../redirectError/redirectErrorSlice';
import { HatApplicationContent } from 'hmi/dist/interfaces/hat-application.interface';
import { getFunction } from '../../api/hattersAPI';
import { HatTool } from '../../interfaces/hat-tool.interface';
import { AUTH_API_URL } from '../../app.config';

type ApplicationsState = {
  parentApp: HatApplicationContent | null;
  dependencyApps: HatApplicationContent[];
  dependencyTools: HatTool[];
};

export const initialState: ApplicationsState = {
  parentApp: null,
  dependencyApps: [],
  dependencyTools: [],
};

export const slice = createSlice({
  name: 'hmi',
  initialState,
  reducers: {
    parentApp: (state, action: PayloadAction<HatApplicationContent>) => {
      state.parentApp = action.payload;
    },
    dependencyApps: (state, action: PayloadAction<HatApplicationContent>) => {
      state.dependencyApps.push(action.payload);
    },
    dependencyTools: (state, action: PayloadAction<HatTool>) => {
      state.dependencyTools.push(action.payload);
    },
  },
});

export const { parentApp, dependencyApps, dependencyTools } = slice.actions;

export const setParentApp =
  (app: HatApplicationContent): AppThunk =>
    (dispatch) => {
      dispatch(parentApp(app));
    };

export const setDependencyApps =
  (app: HatApplicationContent): AppThunk =>
    (dispatch) => {
      dispatch(dependencyApps(app));
    };

export const setDependencyTool =
  (tool: HatTool): AppThunk =>
    (dispatch) => {
      dispatch(dependencyTools(tool));
    };

export const selectParentApp = (state: RootState) => state.hmi.parentApp;
export const selectDependencyApps = (state: RootState) => state.hmi.dependencyApps;
export const selectDependencyTools = (state: RootState) => state.hmi.dependencyTools;

export const getApplication =
  (applicationId: string, lang: string): AppThunk =>
    async (dispatch, getState) => {
      try {
        const currentParentApp = getState().hmi.parentApp;

        if (currentParentApp?.id === applicationId) return;

        let url = `${AUTH_API_URL}/api/applications/${applicationId}`;

        if (lang && lang !== 'en') {
          url += `?lang=${lang}`;
        }

        const app = await get<HatApplicationContent>(url);
        if (app.parsedBody) {
          return dispatch(setParentApp(app.parsedBody));
        }
      } catch (error) {
        const err: RegistrationRedirectError = {
          error: 'application_misconfigured',
          reason: 'application_id_not_found',
        };

        return dispatch(setRedirectError(err));
      }
    };

export const getApplicationDependency =
  (applicationId: string): AppThunk =>
    async (dispatch, getState) => {
      try {
        const currentApplicationDependencies = getState().hmi.dependencyApps;

        if (currentApplicationDependencies.find((dependency) => dependency.id === applicationId)) return;

        let url = `${AUTH_API_URL}/api/applications/${applicationId}`;
        const app = await get<HatApplicationContent>(url);

        if (app.parsedBody) {
          return dispatch(setDependencyApps(app.parsedBody));
        }
      } catch (e) {
      // TODO error handling
        console.log(e);
      }
    };

export const getToolDependency =
  (functionId: string): AppThunk =>
    async (dispatch, getState) => {
      try {
        const currentToolDependencies = getState().hmi.dependencyTools;

        if (currentToolDependencies.find((dependency) => dependency.id === functionId)) return;

        const tool = await getFunction(functionId);

        if (tool.parsedBody) {
          return dispatch(setDependencyTool(tool.parsedBody));
        }
      } catch (e) {
      // TODO error handling
        console.log(e);
      }
    };

export default slice.reducer;
